<script lang="ts" setup>
import { ref } from 'vue'
import {
  DTextField,
  DLoading,
  DModal,
  FileSearch02Icon,
  ArrowRightIcon
} from 'front-library'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { getAuditData, AuditData } from '../core/services/audit'

dayjs.extend(utc)

const props = defineProps<{
  form: string
  id: string
}>()

// Datos componente Loading y Modal
const modalActive = ref(false)
const loading = ref(false)

// Datos
const search = ref()
const { t } = useI18n()

// Datos componente table
const columns = ref([
  { name: 'date', title: t('components.audit.date') },
  { name: 'user', title: t('components.audit.user') },
  { name: 'context', title: t('components.audit.context') },
  { name: 'action', title: t('components.audit.action') },
  { name: 'changes', title: t('components.audit.changes') }
])

// Listar registros de auditoría
const auditData = ref<AuditData[]>([])

const dataCopy = ref(auditData)

// filtrar datos
const filter = (value: string) => {
  const result =
    value !== undefined
      ? auditData.value.filter(item => {
          if (objectValue(item, value)) return true
          for (const change of item.changes) {
            if (objectValue(change, value)) return true
          }
          return false
        })
      : dataCopy.value
  dataCopy.value = result
}

// funcion para filtrar por cada atributo del JSON
function objectValue(obj: any, value: string): boolean {
  return Object.values(obj).some(item => {
    if (Array.isArray(item)) {
      return item.some(subItem => objectValue(subItem, value))
    } else if (typeof item === 'string') {
      const itemValue = item?.toLowerCase()
      const searchValue = value?.toLowerCase()
      return itemValue?.includes(searchValue)
    }
    return item === value
  })
}

const openAudit = async () => {
  modalActive.value = true
  loading.value = true
  auditData.value = await getAuditData(props.id, props.form)
  dataCopy.value = auditData.value
  loading.value = false
}
</script>
<template>
  <DModal
    :open="modalActive"
    @close-modal="modalActive = false"
    size="w-11/12 max-w-screen-2xl">
    <template #form>
      <DLoading v-show="loading" message="..." />
      <div class="w-full">
        <div class="flex justify-between">
          <p class="font-bold text-lg pr-4">
            {{ t('components.audit.title') }}
          </p>
          <div class="w-6/12">
            <DTextField
              class="w-full"
              :placeholder="t('form.button.search') + '...'"
              :icon="'SearchLgIcon'"
              v-model="search"
              @input="filter(search)" />
          </div>
        </div>
        <div
          class="overflow-auto scroll mt-4"
          style="max-height: calc(100vh - 17rem)">
          <table class="w-full">
            <thead class="h-12 sticky top-0">
              <tr class="bg-white border-b border-b-gray-200">
                <th v-for="(column, index) in columns" :key="index">
                  <div
                    class="flex items-center text-base text-gray-600 px-2 whitespace-nowrap"
                    :class="[
                      column.title == 'Cambios'
                        ? 'justify-center'
                        : 'justify-start'
                    ]">
                    {{ column.title }}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="bg-gray-50 shadow-xl">
              <tr
                v-for="(row, index) in (dataCopy as any)"
                :key="index"
                class="border-b border-b-gray-200">
                <td
                  class="px-2 py-0.5 w-28"
                  v-for="(item, i) in columns"
                  :key="i">
                  <div
                    class="w-40"
                    :class="[
                      item.name == 'user'
                        ? 'font-semibold capitalize leading-5 '
                        : item.name == 'context'
                        ? 'overflow-hidden whitespace-nowrap text-ellipsis text-blue-600'
                        : item.name == 'changes'
                        ? 'overflow-hidden whitespace-nowrap text-ellipsis w-full'
                        : 'overflow-hidden whitespace-nowrap text-ellipsis max-w-sx'
                    ]">
                    <template v-if="item.name == 'date'">
                      <p class="leading-5 py-1">
                        <span class="font-semibold" style="font-size: 15px">
                          Hace
                          {{
                            dayjs().diff(row[item.name], 's') < 60
                              ? ' 1 min'
                              : dayjs().diff(row[item.name], 'm') <= 60
                              ? dayjs().diff(row[item.name], 'm') + ' min(s)'
                              : dayjs().diff(row[item.name], 'h') <= 24
                              ? dayjs().diff(row[item.name], 'h') + ' hr(s)'
                              : dayjs().diff(row[item.name], 'd') <= 30
                              ? dayjs().diff(row[item.name], 'd') + ' día(s)'
                              : Math.round(
                                  dayjs().diff(row[item.name], 'M', true)
                                ) <= 12
                              ? Math.round(
                                  dayjs().diff(row[item.name], 'M', true)
                                ) + ' mes(es)'
                              : Math.trunc(
                                  dayjs().diff(row[item.name], 'y', true)
                                ) <= 1
                              ? Math.trunc(
                                  dayjs().diff(row[item.name], 'y', true)
                                ) + ' año'
                              : Math.trunc(
                                  dayjs().diff(row[item.name], 'y', true)
                                ) + ' años'
                          }}
                        </span>
                        <br />
                        <span class="text-gray-500">
                          {{ row[item.name] }}
                        </span>
                      </p>
                    </template>
                    <template v-else-if="item.name == 'changes'">
                      <table class="w-full">
                        <tbody>
                          <tr
                            v-for="(changes, x) in row[item.name]"
                            :key="x"
                            :class="[
                              row[item.name].length - 1 !== x
                                ? 'border-b border-b-gray-200'
                                : ''
                            ]">
                            <td
                              class="font-semibold px-3 text-gray-600 overflow-auto scroll"
                              style="
                                width: 32%;
                                min-width: 140px;
                                max-width: 140px;
                              ">
                              {{ changes['field'] }}
                            </td>
                            <td
                              class="px-3 text-gray-500 overflow-auto scroll"
                              style="
                                width: 32%;
                                min-width: 140px;
                                max-width: 140px;
                              ">
                              {{ changes['oldValue'] }}
                            </td>
                            <td class="px-3" style="width: 48px">
                              <ArrowRightIcon />
                            </td>
                            <td
                              class="px-3 text-gray-500 overflow-auto scroll"
                              style="
                                width: 32%;
                                min-width: 140px;
                                max-width: 140px;
                              ">
                              {{ changes['newValue'] }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </template>
                    <template v-else>
                      {{ row[item.name] }}
                    </template>
                  </div>
                </td>
              </tr>
              <th
                class="bg-white py-5"
                v-if="dataCopy.length == 0"
                :colspan="columns.length">
                <h1>{{ t('table.noData') }}</h1>
              </th>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </DModal>
  <div>
    <Popper
      arrow
      offsetDistance="12"
      content="Registro de auditoria"
      :hover="true"
      placement="left"
      class="tooltip">
      <FileSearch02Icon class="cursor-pointer" @click="openAudit" />
    </Popper>
  </div>
</template>
